import React, { useState, useEffect } from 'react';
import { Card, Button, Input, Select, Form, message, Space, Typography, Divider, Badge } from 'antd';
import { SendOutlined, BellOutlined, MessageOutlined, ApiOutlined, ClockCircleOutlined } from '@ant-design/icons';
import { POST } from '../services';
import websocketService from '../services/websocket';
import { getBreakStatus } from '../services/breakService';

const { TextArea } = Input;
const { Title, Text } = Typography;
const { Option } = Select;

const WebSocketTestPanel = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [breakStatus, setBreakStatus] = useState({
    isOnBreak: false,
    userId: null,
    userName: null,
    type: null,
    startTime: null
  });

  // ดึงสถานะ break
  const fetchBreakStatus = async () => {
    try {
      const response = await getBreakStatus();
      if (response.success) {
        setBreakStatus(response.result);
      }
    } catch (error) {
      console.error('Error fetching break status:', error);
    }
  };

  useEffect(() => {
    fetchBreakStatus();
    
    // Listen for break events
    websocketService.onBreakEvent((eventData) => {
      console.log('Break event in test panel:', eventData);
      if (eventData.type === 'started') {
        setBreakStatus({
          isOnBreak: true,
          userId: eventData.userId,
          userName: eventData.userName,
          type: eventData.breakType,
          startTime: eventData.startTime
        });
      } else if (eventData.type === 'ended') {
        setBreakStatus({
          isOnBreak: false,
          userId: null,
          userName: null,
          type: null,
          startTime: null
        });
      }
    });
  }, []);

  // ส่งข้อความผ่าน WebSocket โดยตรง
  const sendDirectMessage = async (values) => {
    try {
      const { message: messageText } = values;
      websocketService.sendMessage(messageText);
      message.success('ส่งข้อความแล้ว!');
      form.resetFields(['message']);
    } catch (error) {
      message.error('เกิดข้อผิดพลาดในการส่งข้อความ');
    }
  };

  // ส่งแจ้งเตือนผ่าน API (ไปทุกคน)
  const sendBroadcastNotification = async (values) => {
    setLoading(true);
    try {
      const { title, message: messageText, type } = values;
      
      const response = await POST('/api/v1/websocket/broadcast', {
        title,
        message: messageText,
        type
      });

      if (response.success) {
        message.success('ส่งแจ้งเตือนแล้ว!');
        form.resetFields(['title', 'message', 'type']);
      }
    } catch (error) {
      message.error('เกิดข้อผิดพลาดในการส่งแจ้งเตือน');
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  // ส่งแจ้งเตือนให้ผู้ใช้เฉพาะคน
  const sendUserNotification = async (values) => {
    setLoading(true);
    try {
      const { userId, title, message: messageText, type } = values;
      
      const response = await POST('/api/v1/websocket/send-to-user', {
        userId,
        title,
        message: messageText,
        type
      });

      if (response.success) {
        message.success('ส่งแจ้งเตือนเฉพาะบุคคลแล้ว!');
        form.resetFields(['userId', 'title', 'message', 'type']);
      }
    } catch (error) {
      message.error('เกิดข้อผิดพลาดในการส่งแจ้งเตือน');
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ padding: 20, maxWidth: 800, margin: '0 auto' }}>
      <Title level={3}>🔧 WebSocket Test Panel</Title>
      <Text type="secondary">ทดสอบการส่งข้อความและแจ้งเตือนผ่าน WebSocket</Text>
      
      <Divider />

      {/* Break Status Card */}
      <Card 
        title={
          <span>
            <ClockCircleOutlined style={{ color: '#722ed1', marginRight: 8 }} />
            สถานะการพัก
          </span>
        } 
        style={{ marginBottom: 20 }}
        size="small"
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Badge 
            status={breakStatus.isOnBreak ? 'processing' : 'success'} 
            text={breakStatus.isOnBreak ? 'มีคนกำลังพักอยู่' : 'ไม่มีคนพักอยู่'}
          />
          {breakStatus.isOnBreak && (
            <div style={{ marginLeft: 20 }}>
              <Text strong>{breakStatus.userName}</Text>
              <Text style={{ marginLeft: 8, color: '#666' }}>
                กำลัง{breakStatus.type === 'lunch' ? 'พักกินข้าว' : 
                     breakStatus.type === 'coffee' ? 'พักเบรค' : 'เข้างาน'}
              </Text>
              {breakStatus.startTime && (
                <Text style={{ marginLeft: 8, color: '#999', fontSize: 12 }}>
                  ({new Date(breakStatus.startTime).toLocaleTimeString('th-TH')})
                </Text>
              )}
            </div>
          )}
        </div>
      </Card>

      {/* ส่งข้อความโดยตรง */}
      <Card 
        title={
          <span>
            <MessageOutlined style={{ color: '#1890ff', marginRight: 8 }} />
            ส่งข้อความโดยตรง (WebSocket)
          </span>
        } 
        style={{ marginBottom: 20 }}
      >
        <Form form={form} onFinish={sendDirectMessage} layout="vertical">
          <Form.Item
            name="message"
            label="ข้อความ"
            rules={[{ required: true, message: 'กรุณาใส่ข้อความ!' }]}
          >
            <TextArea rows={2} placeholder="พิมพ์ข้อความที่ต้องการส่ง..." />
          </Form.Item>
          <Form.Item>
            <Button 
              type="primary" 
              htmlType="submit" 
              icon={<SendOutlined />}
            >
              ส่งข้อความ
            </Button>
          </Form.Item>
        </Form>
      </Card>

      {/* ส่งแจ้งเตือนให้ทุกคน */}
      <Card 
        title={
          <span>
            <BellOutlined style={{ color: '#52c41a', marginRight: 8 }} />
            ส่งแจ้งเตือนให้ทุกคน (API)
          </span>
        } 
        style={{ marginBottom: 20 }}
      >
        <Form form={form} onFinish={sendBroadcastNotification} layout="vertical">
          <Form.Item
            name="title"
            label="หัวข้อ"
            rules={[{ required: true, message: 'กรุณาใส่หัวข้อ!' }]}
          >
            <Input placeholder="หัวข้อแจ้งเตือน" />
          </Form.Item>
          <Form.Item
            name="message"
            label="ข้อความ"
            rules={[{ required: true, message: 'กรุณาใส่ข้อความ!' }]}
          >
            <TextArea rows={3} placeholder="ข้อความแจ้งเตือน..." />
          </Form.Item>
          <Form.Item
            name="type"
            label="ประเภท"
            initialValue="info"
          >
            <Select>
              <Option value="success">สำเร็จ (เขียว)</Option>
              <Option value="info">ข้อมูล (น้ำเงิน)</Option>
              <Option value="warning">คำเตือน (เหลือง)</Option>
              <Option value="error">ข้อผิดพลาด (แดง)</Option>
            </Select>
          </Form.Item>
          <Form.Item>
            <Button 
              type="primary" 
              htmlType="submit" 
              loading={loading}
              icon={<BellOutlined />}
              style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}
            >
              ส่งแจ้งเตือนให้ทุกคน
            </Button>
          </Form.Item>
        </Form>
      </Card>

      {/* ส่งแจ้งเตือนให้คนเฉพาะ */}
      <Card 
        title={
          <span>
            <ApiOutlined style={{ color: '#722ed1', marginRight: 8 }} />
            ส่งแจ้งเตือนให้ผู้ใช้เฉพาะคน (API)
          </span>
        } 
        style={{ marginBottom: 20 }}
      >
        <Form form={form} onFinish={sendUserNotification} layout="vertical">
          <Form.Item
            name="userId"
            label="User ID"
            rules={[{ required: true, message: 'กรุณาใส่ User ID!' }]}
          >
            <Input placeholder="ID ของผู้ใช้ที่ต้องการส่ง" />
          </Form.Item>
          <Form.Item
            name="title"
            label="หัวข้อ"
            rules={[{ required: true, message: 'กรุณาใส่หัวข้อ!' }]}
          >
            <Input placeholder="หัวข้อแจ้งเตือน" />
          </Form.Item>
          <Form.Item
            name="message"
            label="ข้อความ"
            rules={[{ required: true, message: 'กรุณาใส่ข้อความ!' }]}
          >
            <TextArea rows={3} placeholder="ข้อความแจ้งเตือน..." />
          </Form.Item>
          <Form.Item
            name="type"
            label="ประเภท"
            initialValue="info"
          >
            <Select>
              <Option value="success">สำเร็จ (เขียว)</Option>
              <Option value="info">ข้อมูล (น้ำเงิน)</Option>
              <Option value="warning">คำเตือน (เหลือง)</Option>
              <Option value="error">ข้อผิดพลาด (แดง)</Option>
            </Select>
          </Form.Item>
          <Form.Item>
            <Button 
              type="primary" 
              htmlType="submit" 
              loading={loading}
              icon={<ApiOutlined />}
              style={{ backgroundColor: '#722ed1', borderColor: '#722ed1' }}
            >
              ส่งแจ้งเตือนเฉพาะบุคคล
            </Button>
          </Form.Item>
        </Form>
      </Card>

      <Card size="small" style={{ backgroundColor: '#f6ffed', border: '1px solid #b7eb8f' }}>
        <Text style={{ fontSize: 12 }}>
          💡 <strong>วิธีใช้:</strong><br/>
          1. <strong>ส่งข้อความโดยตรง:</strong> ส่งผ่าน WebSocket ไปยังผู้ใช้ที่เชื่อมต่ออยู่ทั้งหมด<br/>
          2. <strong>ส่งแจ้งเตือนให้ทุกคน:</strong> ใช้ API เพื่อส่งแจ้งเตือนแบบ broadcast<br/>
          3. <strong>ส่งแจ้งเตือนเฉพาะคน:</strong> ใช้ API เพื่อส่งให้ผู้ใช้คนเดียวตาม User ID
        </Text>
      </Card>
    </div>
  );
};

export default WebSocketTestPanel; 